/**
 * PresetSaveDialog Component
 * Dialog for saving custom instrument presets
 */

import { useState, useEffect, useRef } from 'react';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentParameters } from '../../types/instruments';
import { instrumentPresetManager } from '../../services/instruments/InstrumentPresetManager';
import './PresetDialogs.css';

/**
 * Props for PresetSaveDialog component
 */
interface PresetSaveDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Current instrument type */
  instrumentId: InstrumentType;
  /** Current parameter values to save */
  parameters: InstrumentParameters;
  /** Callback when preset is successfully saved */
  onSave?: (presetId: string) => void;
}

/**
 * PresetSaveDialog component
 * Provides a form for saving custom instrument presets with validation
 */
export function PresetSaveDialog({
  isOpen,
  onClose,
  instrumentId,
  parameters,
  onSave,
}: PresetSaveDialogProps) {
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Focus name input when dialog opens
  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen]);

  // Handle click outside dialog to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  /**
   * Reset form state
   */
  const resetForm = () => {
    setPresetName('');
    setPresetDescription('');
    setError('');
    setIsSaving(false);
  };

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Validate preset name
   */
  const validatePresetName = (name: string): string | null => {
    // Check if name is empty
    if (!name.trim()) {
      return 'Preset name is required';
    }

    // Check character limit
    if (name.length > 50) {
      return 'Preset name must be 50 characters or less';
    }

    // Check for duplicate names
    const existingPresets = instrumentPresetManager.getPresetsForInstrument(instrumentId);
    const isDuplicate = existingPresets.some(
      (preset) => preset.name.toLowerCase() === name.trim().toLowerCase()
    );

    if (isDuplicate) {
      return 'A preset with this name already exists';
    }

    return null;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate preset name
    const validationError = validatePresetName(presetName);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Validate description length
    if (presetDescription.length > 200) {
      setError('Description must be 200 characters or less');
      return;
    }

    setIsSaving(true);

    try {
      // Save the preset
      const savedPreset = instrumentPresetManager.savePreset({
        instrumentId,
        name: presetName.trim(),
        description: presetDescription.trim(),
        parameters,
      });

      // Call onSave callback with the new preset ID
      if (onSave) {
        onSave(savedPreset.id);
      }

      // Close dialog and reset form
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preset');
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="preset-dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="save-dialog-title">
      <div className="preset-dialog" ref={dialogRef}>
        <div className="preset-dialog-header">
          <h2 id="save-dialog-title" className="preset-dialog-title">
            Save Preset
          </h2>
          <button
            className="preset-dialog-close"
            onClick={handleClose}
            aria-label="Close dialog"
            type="button"
          >
            ✕
          </button>
        </div>

        <form className="preset-dialog-form" onSubmit={handleSubmit}>
          <div className="preset-form-group">
            <label htmlFor="preset-name" className="preset-form-label">
              Preset Name <span className="preset-form-required">*</span>
            </label>
            <input
              ref={nameInputRef}
              id="preset-name"
              type="text"
              className="preset-form-input"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g., Bright Lead, Deep Bass"
              maxLength={50}
              required
              aria-required="true"
              aria-invalid={!!error}
              aria-describedby={error ? 'preset-error' : undefined}
            />
            <span className="preset-form-hint">
              {presetName.length}/50 characters
            </span>
          </div>

          <div className="preset-form-group">
            <label htmlFor="preset-description" className="preset-form-label">
              Description
            </label>
            <textarea
              id="preset-description"
              className="preset-form-textarea"
              value={presetDescription}
              onChange={(e) => setPresetDescription(e.target.value)}
              placeholder="Describe the sound characteristics and use cases..."
              maxLength={200}
              rows={3}
            />
            <span className="preset-form-hint">
              {presetDescription.length}/200 characters
            </span>
          </div>

          {error && (
            <div id="preset-error" className="preset-form-error" role="alert">
              {error}
            </div>
          )}

          <div className="preset-dialog-actions">
            <button
              type="button"
              className="preset-btn preset-btn-secondary"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="preset-btn preset-btn-primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Preset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
